import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { pixelRatio2d } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { ESceneMode } from '@/editor/types';
import { IShapeSeg2d } from '@/editor/types/shape';
import type { BoundingBox } from '@/editor/utils/dxf/types';

/**
 * 吸附候选的过滤矩形，世界坐标（mm）。
 * `null` 表示不过滤：3D 场景、或拿不到 2D 相机时退化为「全部候选参与」的旧行为。
 */
export type TAbsorbViewportBounds = BoundingBox | null;

export type TAbsorbViewportBoundsProvider = () => TAbsorbViewportBounds;

/** 参与视口过滤的吸附线 */
export interface IViewportFilterable {
  seg: IShapeSeg2d;
  /**
   * 视口过滤用的包围盒，缺省时按 `seg` 现算。
   *
   * 仅在 `seg` 无法代表实体位置时才需要显式给出：box 中心线的 seg 只是一段表达方向的短线段，
   * 必须取所属 box 的包围盒，否则同一个 box 的中心线与边界线可见性会不一致。
   */
  bounds?: BoundingBox;
}

export function getPointsBounds(points: IPoint2d[]): BoundingBox {
  let { x: minX, y: minY } = points[0];
  let { x: maxX, y: maxY } = points[0];

  for (let index = 1; index < points.length; index++) {
    const { x, y } = points[index];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  return { minX, minY, maxX, maxY };
}

/**
 * 弧的真实包围盒：起终点 + 落在扫掠范围内的轴向极值点。
 *
 * 不能用弦的包围盒代替，那样「只有弧肚子在视口内」的弧墙会被判为不可见；也不能用整圆包围盒，
 * 墙体弧都是浅弧（chord 10m + sagitta 0.1m 的半径就有 125m），整圆包围盒会让它永远可见。
 */
function getArcSegBounds(arc: ArcSeg2d): BoundingBox {
  const { center, radius } = arc;
  const sweepAngle = arc.getSweepAngle();
  const points: IPoint2d[] = [arc.start, arc.end];

  const axisPoints: IPoint2d[] = [
    { x: center.x + radius, y: center.y },
    { x: center.x, y: center.y + radius },
    { x: center.x - radius, y: center.y },
    { x: center.x, y: center.y - radius }
  ];
  axisPoints.forEach((point) => {
    if (arc.getPointSweepAngle(point) <= sweepAngle) points.push(point);
  });

  return getPointsBounds(points);
}

export function getSegBounds(seg: IShapeSeg2d): BoundingBox {
  if (seg instanceof ArcSeg2d) return getArcSegBounds(seg);

  return getPointsBounds([seg.start, seg.end]);
}

function isBoundsIntersected(a: BoundingBox, b: BoundingBox) {
  return !(a.maxX < b.minX || a.minX > b.maxX || a.maxY < b.minY || a.minY > b.maxY);
}

/**
 * 当前 2D 视口对应的世界矩形（mm）。
 *
 * 相机 frustum 是渲染坐标，世界坐标 = 渲染坐标 × `pixelRatio2d`；缩放后的半宽 = (right - left) / 2 / zoom。
 * 相机 position 即视口中心：2D 相机 `lookAt(0, 0, 0)` 且控制器关闭了旋转、开启了 screenSpacePanning，
 * 平移时相机与 target 同步移动。
 */
export function getAbsorbViewportBounds(): TAbsorbViewportBounds {
  if (globalStores.sceneMode !== ESceneMode.scene2d) return null;

  const camera = globalStores.editorStateStore?.sceneInfo?.scene2d?.camera;
  if (!camera) return null;

  const halfWidth = ((camera.right - camera.left) / 2 / camera.zoom) * pixelRatio2d;
  const halfHeight = ((camera.top - camera.bottom) / 2 / camera.zoom) * pixelRatio2d;
  const centerX = camera.position.x * pixelRatio2d;
  const centerY = camera.position.y * pixelRatio2d;
  if (![halfWidth, halfHeight, centerX, centerY].every(Number.isFinite)) return null;
  if (halfWidth <= 0 || halfHeight <= 0) return null;

  return {
    minX: centerX - halfWidth,
    minY: centerY - halfHeight,
    maxX: centerX + halfWidth,
    maxY: centerY + halfHeight
  };
}

/**
 * 过滤矩形 = 视口 ∪ 各参考点。
 *
 * 并入参考点是为了保证「参考点所在处的候选不被误杀」：按 shift 强制正交时 rawPoint 会被投影到
 * 正交线原点所在的行/列上，而该原点（上一个落点）平移后可能已在屏幕外。
 */
export function getAbsorbFilterBounds(
  viewportBounds: TAbsorbViewportBounds,
  referencePoints: IPoint2d[]
): TAbsorbViewportBounds {
  if (!viewportBounds) return null;
  if (!referencePoints.length) return { ...viewportBounds };

  const referenceBounds = getPointsBounds(referencePoints);

  return {
    minX: Math.min(viewportBounds.minX, referenceBounds.minX),
    minY: Math.min(viewportBounds.minY, referenceBounds.minY),
    maxX: Math.max(viewportBounds.maxX, referenceBounds.maxX),
    maxY: Math.max(viewportBounds.maxY, referenceBounds.maxY)
  };
}

/** bounds 为 null 时原样返回入参（同一引用），保证 3D / 降级路径零开销零行为变化 */
export function filterVisibleInfos<T extends IViewportFilterable>(infos: T[], bounds: TAbsorbViewportBounds): T[] {
  if (!bounds) return infos;

  return infos.filter((info) => isBoundsIntersected(info.bounds ?? getSegBounds(info.seg), bounds));
}
